// src/components/TermsOfService.tsx
import React, { useState } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';

interface TermsOfServiceProps {
  onAccept: () => void;
  onDecline?: () => void;
}

export function TermsOfService({ onAccept, onDecline }: TermsOfServiceProps) {
  const [hasScrolled, setHasScrolled] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom) {
      setHasScrolled(true);
    }
  };

  const canAccept = hasScrolled; // Can accept when scrolled to bottom

  return (
    <div className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" style={{ pointerEvents: 'auto' }}>
      <div className="w-full max-w-3xl bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl border-2 border-amber-400/30 shadow-2xl" style={{ height: '90vh', position: 'relative', pointerEvents: 'auto' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4" style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, pointerEvents: 'auto' }}>
          <h2 className="text-2xl font-bold text-white">WHIPT Terms of Service & Community Guidelines</h2>
          <p className="text-sm text-amber-100 mt-1">Please read carefully before using WHIPT</p>
          {!canAccept && (
            <div className="mt-3 bg-white/20 rounded-lg px-4 py-2 text-center">
              <p className="text-white font-semibold text-lg">
                📜 Please scroll to the bottom to enable the Accept button
              </p>
            </div>
          )}
        </div>

        {/* Content - Scrollable - ABSOLUTE POSITIONING */}
        <div 
          onScroll={handleScroll}
          style={{ 
            position: 'absolute',
            top: '160px',
            bottom: '120px',
            left: '0',
            right: '0',
            overflowY: 'scroll',
            padding: '24px',
            color: '#cbd5e1',
            WebkitOverflowScrolling: 'touch',
            border: '2px solid #f59e0b',
            pointerEvents: 'auto',
            cursor: 'default',
            touchAction: 'pan-y'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Warning */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-red-300 mb-1">Important Notice</h3>
              <p className="text-sm text-red-200">
                WHIPT is an academic organization tool. It is NOT intended for emergency communications, 
                healthcare advice, or any life-critical situations.
              </p>
            </div>
          </div>

          {/* Terms */}
          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">1. Acceptance of Terms</h3>
            <p className="text-sm leading-relaxed">
              By accessing and using WHIPT ("Where'd I Put That?"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use this application.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">2. Account Responsibilities</h3>
            <ul className="list-disc list-inside space-y-2 text-sm">
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You must provide accurate and complete information during registration</li>
              <li>You must be at least 13 years old to use WHIPT</li>
              <li>One account per person - no sharing accounts</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">3. Community Guidelines & Chat Rules</h3>
            <div className="space-y-3 text-sm">
              <p className="font-semibold text-white">Be Respectful & Professional:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Treat all classmates with respect and kindness</li>
                <li>No harassment, bullying, hate speech, or discriminatory language</li>
                <li>Respect diverse opinions and backgrounds</li>
                <li>Keep academic discussions constructive and professional</li>
              </ul>

              <p className="font-semibold text-white mt-4">Content Standards:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>No explicit, violent, or inappropriate content</li>
                <li>No spam, advertisements, or self-promotion without permission</li>
                <li>No sharing of copyrighted materials without authorization</li>
                <li>No sharing of personal information of others without consent</li>
                <li>Keep conversations relevant to academic purposes</li>
              </ul>

              <p className="font-semibold text-white mt-4">File Sharing Rules:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Only share files relevant to coursework or group projects</li>
                <li>Respect copyright and intellectual property rights</li>
                <li>Do not share malicious files or viruses</li>
                <li>Maximum file size limits apply (enforced by browser)</li>
              </ul>

              <p className="font-semibold text-white mt-4">WHIPT Rooms Guidelines:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Create rooms for legitimate academic or organizational purposes</li>
                <li>Room creators are responsible for moderating their rooms</li>
                <li>Use appropriate room names and descriptions</li>
                <li>Respect room purposes and stay on-topic</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">4. Privacy & Data</h3>
            <ul className="list-disc list-inside space-y-2 text-sm">
              <li>Your data is stored locally in your browser and on our servers</li>
              <li>Messages, files, and documents you share may be visible to other users in the same rooms</li>
              <li>We do not sell your personal information to third parties</li>
              <li>Online status is visible to others unless you toggle it off</li>
              <li>You can request account deletion at any time</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">5. Virtual Rooms & Meetings</h3>
            <ul className="list-disc list-inside space-y-2 text-sm">
              <li>Virtual rooms use peer-to-peer connections (WebRTC)</li>
              <li>Record meetings only with consent from all participants</li>
              <li>AI transcription is optional and stored locally</li>
              <li>Be aware that others can take screenshots during video calls</li>
              <li>Dress appropriately and maintain professional conduct in video calls</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">6. Prohibited Activities</h3>
            <ul className="list-disc list-inside space-y-2 text-sm">
              <li>Academic dishonesty or cheating</li>
              <li>Impersonating others</li>
              <li>Attempting to hack, exploit, or compromise the system</li>
              <li>Using automated bots or scripts</li>
              <li>Collecting data from other users without permission</li>
              <li>Disrupting or interfering with other users' experience</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">7. Consequences of Violations</h3>
            <p className="text-sm leading-relaxed mb-2">
              Violations of these terms may result in:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm ml-4">
              <li>Warning from administrators</li>
              <li>Temporary suspension of account</li>
              <li>Permanent ban from WHIPT</li>
              <li>Reporting to educational institutions if applicable</li>
              <li>Legal action in severe cases</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">8. Limitation of Liability</h3>
            <p className="text-sm leading-relaxed">
              WHIPT is provided "as is" without warranties. We are not liable for any data loss, 
              missed deadlines, technical issues, or any damages arising from use of this application. 
              You are responsible for backing up your own work and meeting your own deadlines.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">9. Changes to Terms</h3>
            <p className="text-sm leading-relaxed">
              We reserve the right to modify these terms at any time. Continued use of WHIPT 
              after changes constitutes acceptance of new terms.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-amber-300 mb-3">10. Contact & Reporting</h3>
            <p className="text-sm leading-relaxed">
              To report violations, technical issues, or ask questions about these terms, 
              please contact administrators through the app settings or email support.
            </p>
          </section>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-700 p-6 bg-slate-900/50" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10, pointerEvents: 'auto' }}>
          <p className="text-xs text-slate-400 mb-4 text-center">
            By clicking "I Accept", you acknowledge that you have read and agree to these Terms of Service 
            and Community Guidelines.
          </p>
          <div className="flex gap-3">
            {onDecline && (
              <button
                onClick={onDecline}
                className="flex-1 px-6 py-3 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors"
                style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              >
                Decline
              </button>
            )}
            <button
              onClick={onAccept}
              disabled={!canAccept}
              title={!canAccept ? "Please scroll to the bottom to accept" : "Click to accept"}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg hover:shadow-amber-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ pointerEvents: 'auto', cursor: canAccept ? 'pointer' : 'not-allowed' }}
            >
              <Check className="w-5 h-5" />
              <span>I Accept</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Check if user has accepted terms
export function hasAcceptedTerms(): boolean {
  return localStorage.getItem('whipt_terms_accepted') === 'true';
}

// Mark terms as accepted
export function acceptTerms(): void {
  localStorage.setItem('whipt_terms_accepted', 'true');
  localStorage.setItem('whipt_terms_accepted_date', new Date().toISOString());
}
